import { collection, doc, writeBatch } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { NOM35_G1_ITEMS, NOM35_G3_ITEMS } from '@/config/nom35Items';
import { CLIMATE_QUESTIONS } from '@/config/climateQuestions';
import { scoreNom35G3 } from '@/lib/nom35';

export const DEMO_AUTH_UID = 'h5lbHIvI8bUaFex1f79uXqOeQxk2';
export const DEMO_EMAIL = 'demo@psytalent.com';

type Nom35Profile = 'critical' | 'average' | 'healthy';
type ClimateProfile = 'critical' | 'average' | 'healthy';

function pick<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)] as T;
}

function clampInt(n: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, Math.round(n)));
}

function randomFrom(min: number, max: number): number {
  return min + Math.random() * (max - min);
}

function isoNow(): string {
  return new Date().toISOString();
}

function buildNom35G3Answers(profile: Nom35Profile): Record<string, number> {
  // Scoring engine uses:
  // - reverse: value = raw
  // - non-reverse: value = 4 - raw
  // High risk => higher total values.
  const answers: Record<string, number> = {};
  for (const item of NOM35_G3_ITEMS) {
    let baseValue: number;
    if (profile === 'critical') baseValue = 3.6; // tends to Alto/Muy Alto
    else if (profile === 'average') baseValue = 1.25; // tends to Medio
    else baseValue = 0.6; // tends to Nulo/Bajo

    // Add small jitter so employees don't look identical.
    const targetValue = clampInt(randomFrom(baseValue - 0.5, baseValue + 0.5), 0, 4);

    const raw = item.reverse ? targetValue : 4 - targetValue;
    answers[item.id] = clampInt(raw, 0, 4);
  }
  return answers;
}

function buildNom35G1Answers(profile: Nom35Profile): Record<string, number> {
  // Guía I es binaria (0/1). Para "critical" ponemos más "Sí" para simular ATS.
  const answers: Record<string, number> = {};
  for (const item of NOM35_G1_ITEMS) {
    const pYes = profile === 'critical' ? 0.35 : profile === 'average' ? 0.12 : 0.05;
    answers[item.id] = Math.random() < pYes ? 1 : 0;
  }
  return answers;
}

function buildClimateAnswers(profile: ClimateProfile): Record<string, number | string> {
  const answers: Record<string, number | string> = {};
  for (const q of CLIMATE_QUESTIONS) {
    if (q.type === 'likert') {
      const v =
        profile === 'critical'
          ? pick([1, 1, 2])
          : profile === 'average'
            ? pick([2, 3, 3, 4])
            : pick([4, 5, 5]);
      answers[q.id] = v;
      continue;
    }
    if (q.type === 'enps') {
      const v =
        profile === 'critical'
          ? pick([0, 1, 2, 3, 4, 5, 6])
          : profile === 'average'
            ? pick([6, 7, 7, 8])
            : pick([9, 10, 10]);
      answers[q.id] = v;
      continue;
    }
    // open
    answers[q.id] =
      profile === 'critical'
        ? pick([
            'Se necesita mejorar la comunicación y la carga de trabajo.',
            'Hay estrés constante y falta de claridad en prioridades.',
            'La colaboración entre áreas es baja.',
          ])
        : profile === 'average'
          ? pick([
              'En general bien, pero hay oportunidades de mejora.',
              'Me gustaría más claridad en procesos y comunicación.',
              'Podría mejorar el reconocimiento.',
            ])
          : pick([
              'Buen ambiente y apoyo del equipo.',
              'Me siento motivado y con oportunidades de crecimiento.',
              'La comunicación es clara y el liderazgo es positivo.',
            ]);
  }
  return answers;
}

function computeNom35Result(answers: Record<string, number>): { total: number; riskLevel: string; maxPossible: number } {
  const itemsForEngine = NOM35_G3_ITEMS.map((i) => ({ reverse: i.reverse }));
  const answersArray = NOM35_G3_ITEMS.map((item) => answers[item.id] ?? 0);
  const r = scoreNom35G3(answersArray, itemsForEngine);
  return { total: r.total, riskLevel: r.riskLevel, maxPossible: r.maxPossible };
}

export async function generateDemoCompanyWithAssessments(): Promise<{
  companyId: string;
  nom35CampaignId: string;
  climateCampaignId: string;
  employeesCreated: number;
}> {
  const now = isoNow();

  const companyRef = doc(collection(db, 'companies'));
  const companyId = companyRef.id;

  const userRef = doc(db, 'users', DEMO_AUTH_UID);

  const nom35CampaignRef = doc(collection(db, 'assessment_campaigns'));
  const climateCampaignRef = doc(collection(db, 'assessment_campaigns'));

  const batch = writeBatch(db);

  // Company
  batch.set(companyRef, {
    name: 'Corporativo Demo Total',
    industry: 'Servicios',
    country: 'MX',
    branding: {},
    createdAt: now,
  });

  // AppUser (Cuenta Semilla)
  batch.set(
    userRef,
    {
      companyId,
      role: 'admin',
      displayName: 'Admin Demo',
      email: DEMO_EMAIL,
      createdAt: now,
    },
    { merge: true }
  );

  // Campaigns
  batch.set(nom35CampaignRef, {
    companyId,
    title: 'NOM35',
    type: 'NOM35',
    status: 'closed',
    createdAt: now,
    closedAt: now,
  });

  batch.set(climateCampaignRef, {
    companyId,
    title: 'CLIMATE',
    type: 'CLIMATE',
    status: 'closed',
    createdAt: now,
    closedAt: now,
  });

  const departments = ['Operaciones', 'Ventas', 'RH', 'TI', 'Finanzas', 'Atención al Cliente'];
  const positions = ['Analista', 'Coordinador', 'Especialista', 'Ejecutivo', 'Supervisor', 'Asistente'];
  const sexes: Array<'Masculino' | 'Femenino' | 'Otro'> = ['Masculino', 'Femenino', 'Otro'];
  const maritalStatuses = ['Soltero(a)', 'Casado(a)', 'Unión libre', 'Divorciado(a)'];
  const educationLevels = ['Bachillerato', 'Técnico', 'Licenciatura', 'Maestría'];
  const jobTypes = ['Operativo', 'Administrativo', 'Técnico', 'Comercial'];
  const contractTypes = ['Indefinido', 'Temporal', 'Por proyecto', 'Honorarios'];

  const employeeRefs: Array<{ refId: string; firstName: string; lastName: string; profile: Nom35Profile }> = [];

  // 20 críticos, 30 promedio, 50 saludables
  const profiles: Nom35Profile[] = [
    ...Array.from({ length: 20 }, () => 'critical' as const),
    ...Array.from({ length: 30 }, () => 'average' as const),
    ...Array.from({ length: 50 }, () => 'healthy' as const),
  ];

  // Shuffle profiles so the dataset doesn't look grouped.
  for (let i = profiles.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const tmp = profiles[i];
    profiles[i] = profiles[j] as Nom35Profile;
    profiles[j] = tmp as Nom35Profile;
  }

  for (let i = 0; i < 100; i++) {
    const createdAt = isoNow();
    const completedAt = createdAt;

    const employeeRef = doc(collection(db, 'employees'));
    const employeeId = employeeRef.id;

    const firstName = `Empleado${String(i + 1).padStart(3, '0')}`;
    const lastName = 'Demo';
    const email = `empleado${String(i + 1).padStart(3, '0')}@demo.psytalent.com`.toLowerCase();
    const department = pick(departments);
    const position = pick(positions);
    const dateOfHire = new Date(Date.now() - Math.floor(randomFrom(30, 2000)) * 24 * 60 * 60 * 1000)
      .toISOString()
      .slice(0, 10);
    const birthDate = new Date(Date.now() - Math.floor(randomFrom(22 * 365, 61 * 365)) * 24 * 60 * 60 * 1000)
      .toISOString()
      .slice(0, 10);
    const sex = pick(sexes);
    const maritalStatus = pick(maritalStatuses);
    const educationLevel = pick(educationLevels);
    const jobType = pick(jobTypes);
    const contractType = pick(contractTypes);

    const profile = profiles[i] ?? 'healthy';

    batch.set(employeeRef, {
      companyId,
      firstName,
      lastName,
      email,
      phone: null,
      position,
      department,
      dateOfHire,
      hireDate: dateOfHire,
      sex,
      birthDate,
      maritalStatus,
      educationLevel,
      jobType,
      contractType,
      isActive: true,
      createdAt,
    });

    const employeeName = `${firstName} ${lastName}`.trim();

    // NOM-035 completed (G1 -> G3 encadenado)
    const nom35G3Ref = doc(collection(db, 'nom35_assessments'));
    const nom35G1Ref = doc(collection(db, 'nom35_assessments'));

    const g3Answers = buildNom35G3Answers(profile);
    const g3Result = computeNom35Result(g3Answers);
    const g1Answers = buildNom35G1Answers(profile);

    batch.set(nom35G3Ref, {
      companyId,
      campaignId: nom35CampaignRef.id,
      employeeId,
      employeeName,
      guideType: 'NOM35_G3',
      status: 'completed',
      createdAt,
      completedAt,
      answers: g3Answers,
      resultScore: g3Result.total,
      resultLevel: g3Result.riskLevel,
      requiresClinicalAttention: false,
    });

    batch.set(nom35G1Ref, {
      companyId,
      campaignId: nom35CampaignRef.id,
      employeeId,
      employeeName,
      guideType: 'NOM35_G1',
      status: 'completed',
      createdAt,
      completedAt,
      answers: g1Answers,
      requiresClinicalAttention: false,
      nextAssessmentId: nom35G3Ref.id,
    });

    // Climate completed
    const climateRef = doc(collection(db, 'climate_assessments'));
    const climateProfile: ClimateProfile = profile;
    const climateAnswers = buildClimateAnswers(climateProfile);

    batch.set(climateRef, {
      companyId,
      campaignId: climateCampaignRef.id,
      employeeId,
      employeeName,
      status: 'completed',
      createdAt,
      completedAt,
      answers: climateAnswers,
    });

    employeeRefs.push({ refId: employeeId, firstName, lastName, profile });
  }

  await batch.commit();

  return {
    companyId,
    nom35CampaignId: nom35CampaignRef.id,
    climateCampaignId: climateCampaignRef.id,
    employeesCreated: 100,
  };
}

